// v4.25 - 2026-10-05 - SCREENS-301: load() switched from raw fetch to the authenticated axios
//          client (api) so the Clerk bearer attaches on the backend call; the previous raw
//          fetch sent no Authorization and validate_tenant 401'd (page then showed "Failed
//          to load audit trail" even for authenticated tenants).
// v3.94 - MISSED-ID-04 EDGS: Employment risk audit trail page - 2026-04-25
//
// Tenant-scoped, paginated table of employment-decision risk events.
// Question text is never shown (only hashed in DB). Accessible from Settings sidebar.

import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    Button,
    Divider,
} from '@mui/material';
import api from '../services/api';

interface AuditEntry {
    id: number;
    session_id: string | null;
    intent: string;
    risk_level: string;
    risk_category: string;
    disclaimer_text: string;
    was_reframed: boolean;
    requires_manual_review: boolean;
    created_at: string;
}

interface AuditResponse {
    entries: AuditEntry[];
    total: number;
    limit: number;
    offset: number;
}

const LIMIT = 50;

function riskChipColor(level: string): 'default' | 'warning' | 'error' {
    if (level === 'HIGH') return 'error';
    if (level === 'MEDIUM') return 'warning';
    return 'default';
}

export default function AuditTrail() {
    const [data, setData] = useState<AuditResponse | null>(null);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async (o: number) => {
        setLoading(true);
        setError(null);
        try {
            // v4.25 - SCREENS-301: axios client attaches the Clerk bearer via its request
            //         interceptor; baseURL is /api, so the final path is /api/audit/employment-risk. - 2026-10-05
            const res = await api.get<AuditResponse>('/audit/employment-risk', {
                params: { limit: LIMIT, offset: o },
            });
            setData(res.data);
        } catch (e: any) {
            setError('Failed to load audit trail. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(offset); }, [load, offset]);

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
            <Typography variant="h5" fontWeight={600} mb={0.5}>
                Workforce Data Audit Trail
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                A record of questions involving workforce data. Question text is never stored — only the topic and risk level.
                This log is retained for compliance and cannot be deleted.
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Topic</TableCell>
                                    <TableCell>Risk Level</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Reframed</TableCell>
                                    <TableCell>Review Flag</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data?.entries ?? []).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography variant="body2" color="text.secondary" py={2}>
                                                No workforce data questions logged yet.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    (data?.entries ?? []).map((row) => (
                                        <TableRow key={row.id} hover>
                                            <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                                                {new Date(row.created_at).toLocaleString()}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.8rem' }}>
                                                {row.intent || '—'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.risk_level}
                                                    size="small"
                                                    color={riskChipColor(row.risk_level)}
                                                    variant={row.risk_level === 'LOW' ? 'outlined' : 'filled'}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.8rem' }}>
                                                {row.risk_category.replace(/_/g, ' ')}
                                            </TableCell>
                                            <TableCell>
                                                {row.was_reframed ? (
                                                    <Chip label="Yes" size="small" color="info" variant="outlined" />
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled">No</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {row.requires_manual_review ? (
                                                    <Chip label="Flagged" size="small" color="error" variant="outlined" />
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled">—</Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
                        <Typography variant="caption" color="text.secondary">
                            {data ? `Showing ${data.offset + 1}–${Math.min(data.offset + data.entries.length, data.total)} of ${data.total}` : ''}
                        </Typography>
                        <Box display="flex" gap={1}>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={offset === 0}
                                onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                            >
                                Previous
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={!data || offset + LIMIT >= data.total}
                                onClick={() => setOffset(offset + LIMIT)}
                            >
                                Next
                            </Button>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
}
